import { Box, Stack, Typography, styled as muiStyled } from '@mui/material';
import { ChevronRight } from '@mui/icons-material';
import { abyssColors, drawerBackground, versionHistoryColors } from '@/theme/colors';

export const HistoryContainer = muiStyled(Stack)(() => ({
  height: '100%',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  background: drawerBackground,
}));

export const HistoryList = muiStyled(Box)(() => ({
  width: '100%',
  flex: '1 1 auto',
  overflowY: 'auto',
  position: 'relative',
  padding: '16px',
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: abyssColors.primary[300],
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: abyssColors.primary[400],
  },
}));

export const HistoryEntry = muiStyled(Stack, {
  shouldForwardProp: (prop) => prop !== 'isFirst' && prop !== 'isLast',
})<{ isFirst?: boolean; isLast?: boolean }>(({ isFirst, isLast }) => ({
  position: 'relative',
  minHeight: 50,
  paddingLeft: 25,
  paddingBottom: 20,
  display: 'flex',
  flexDirection: 'row',

  '&::before': {
    content: '""',
    position: 'absolute',
    left: 5,
    top: 16,
    width: 12,
    height: 12,
    borderRadius: '50%',
    border: `2px solid ${isFirst ? abyssColors.primary[400] : abyssColors.primary[200]}`,
    backgroundColor: isFirst ? abyssColors.primary[400] : abyssColors.primary[50],
    zIndex: 2,
  },

  '&::after': {
    content: '""',
    position: 'absolute',
    left: 10,
    top: isFirst ? 22 : 0,
    bottom: isLast ? 'auto' : 0,
    height: isFirst && isLast ? '0' : isLast ? '16px' : 'auto',
    width: isFirst && isLast ? '0' : '2px',
    backgroundColor: isFirst && isLast ? 'transparent' : abyssColors.neutral[200],
    zIndex: 1,
  },
}));

export const EntryContent = muiStyled(Box)(() => ({
  flex: 1,
  marginTop: 10,
  paddingLeft: 10,
}));

export const CapsuleButton = muiStyled(Box, {
  shouldForwardProp: (prop) => prop !== 'isExpanded',
})<{ isExpanded: boolean }>(({ isExpanded }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  paddingLeft: 8,
  paddingRight: 8,
  border: '2px solid',
  borderColor: isExpanded ? versionHistoryColors.POF.text : abyssColors.primary[200],
  color: isExpanded ? versionHistoryColors.POF.text : abyssColors.primary[600],
  borderRadius: '24px',
  cursor: 'pointer',
  backgroundColor: abyssColors.primary[100],
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
}));

export const CapsuleContent = muiStyled(Box)(() => ({
  marginTop: 12,
  background: abyssColors.primary[100],
  borderRadius: '4px',
  padding: '4px',
  gap: '8px',
}));

export const CapsuleItem = muiStyled(Box)(() => ({
  padding: 4,
  borderRadius: 4,
  '&:last-child': {
    marginBottom: 0,
  },
}));

export const CapsuleText = muiStyled(Typography)(() => ({
  fontSize: '12px',
  color: abyssColors.primary[500],
  fontWeight: 400,
}));

export const CapsuleIcon = muiStyled(ChevronRight, {
  shouldForwardProp: (prop) => prop !== 'isExpanded',
})<{ isExpanded: boolean }>(({ isExpanded }) => ({
  transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
  transition: 'transform 0.2s',
  fontSize: '1.2rem',
  color: isExpanded ? versionHistoryColors.POF.text : abyssColors.primary[600],
}));

export const CapsuleLabel = muiStyled(Typography)(() => ({
  marginLeft: 4,
  fontWeight: 500,
  fontSize: '12px',
}));
